import React, { useState, useEffect, useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaArrowRight } from './Icon';
import { useTheme } from '../context/ThemeContext';
import { useLang } from '../context/LanguageContext';
import { getLocalizedServices } from '../utils/servicesData';
import LanguagePicker from './LanguagePicker';
import './Navbar.css';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { theme } = useTheme();
  const { t } = useLang();
  const location = useLocation();
  const services = useMemo(() => getLocalizedServices(t), [t]);

  const navLinks = [
    { path: '/',           label: t('navbar.home') },
    { path: '/a-propos',   label: t('navbar.about') },
    { path: '/services',   label: t('navbar.services'), hasDropdown: true },
    { path: '/portfolio',  label: t('navbar.portfolio') },
    { path: '/apporteurs', label: t('navbar.apporteurs') },
    { path: '/contact',    label: t('navbar.contact') },
  ];

  useEffect(() => {
    // Listener passive + throttle via rAF :
    //  - {passive: true} : laisse le scroll natif fluide (le navigateur n'attend pas notre callback)
    //  - requestAnimationFrame : ne réagit qu'une fois par frame, jamais entre deux paints
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 60);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
  }, [location]);

  return (
    <nav className={`nx-navbar ${scrolled ? 'scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
      <div className="container nx-navbar-inner">
        <Link to="/" className="nx-logo">
          <img src="/logo.png" alt="Nexia Digital" className="nx-logo-img" width="140" height="40" fetchpriority="high" />
        </Link>

        <ul className={`nx-nav-links ${menuOpen ? 'active' : ''}`}>
          {navLinks.map(({ path, label, hasDropdown }) => (
            hasDropdown ? (
              <li
                key={path}
                className={`nx-has-dropdown ${servicesOpen ? 'open' : ''}`}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <div className="nx-dropdown-trigger">
                  <NavLink to={path} className={({ isActive }) => isActive ? 'active' : ''}>
                    {label}
                  </NavLink>
                  <button
                    type="button"
                    className="nx-dropdown-toggle"
                    aria-label="Voir les services"
                    aria-expanded={servicesOpen}
                    onClick={(e) => { e.preventDefault(); setServicesOpen(o => !o); }}
                  >
                    <span className={`nx-caret ${servicesOpen ? 'open' : ''}`} />
                  </button>
                </div>
                <div className={`nx-dropdown ${servicesOpen ? 'open' : ''}`}>
                  <ul className="nx-dropdown-list">
                    {services.map((service) => (
                      <li key={service.slug}>
                        <Link to={`/services/${service.slug}`} className="nx-dropdown-item">
                          <span className="nx-dropdown-icon" style={{ color: service.color }}>
                            <i className={service.icon} />
                          </span>
                          <span className="nx-dropdown-text">
                            <span className="nx-dropdown-title">{service.title}</span>
                            <span className="nx-dropdown-tagline">{service.tagline}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ) : (
              <li key={path}>
                <NavLink to={path} end={path === '/'} className={({ isActive }) => isActive ? 'active' : ''}>
                  {label}
                </NavLink>
              </li>
            )
          ))}
          <li className="mobile-cta">
            <Link to="/devis" className="btn-primary">{t('navbar.cta')}</Link>
          </li>
        </ul>

        <div className="nx-nav-right">
          <LanguagePicker />
          <Link to="/devis" className="btn-primary nav-cta">
            {t('navbar.cta')} <FaArrowRight />
          </Link>
          <button
            className={`nx-hamburger ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {menuOpen && <div className="nx-menu-overlay" onClick={() => setMenuOpen(false)} />}
    </nav>
  );
}
